import { z } from 'zod';
import { FORMATTERS } from '@docgen/schema';
import { ItemTextSlot, ListItemControls, TextSlot, useEditState, useProp, useTextProp } from '@docgen/sdk';
import type { BlockDef } from '../types.js';
import { asText, pageAnchorId } from '../style.js';

const asArray = (v: unknown): Record<string, unknown>[] =>
  Array.isArray(v) ? (v as Record<string, unknown>[]) : [];

interface ColumnDef { key?: unknown; label?: unknown; align?: unknown; format?: unknown }

function formatCell(v: unknown, format: unknown): string {
  const fmt = typeof format === 'string' ? FORMATTERS[format] : undefined;
  return fmt ? fmt(v) : asText(v);
}

const ROW_CLASS: Record<string, string> = { discount: 'disc', subtotal: 'sub' };

/** The numeric amount from a row's value ("– $1,150.00" → 1150), or null. */
function parseAmount(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? Math.abs(value) : null;
  if (typeof value !== 'string') return null;
  const match = value.replace(/,/g, '').match(/\d+(?:\.\d+)?/);
  return match ? Number(match[0]) : null;
}

/**
 * The live total: normal rows add, discount rows subtract, subtotal rows are
 * skipped. Keeps the total in step when rows are edited by hand. Null when no
 * row carries a parseable amount, so a non-money table falls back to totalValue.
 */
function computeTotal(rows: readonly Record<string, unknown>[]): number | null {
  let sum = 0;
  let any = false;
  for (const row of rows) {
    const kind = asText(row['kind']) || 'normal';
    if (kind === 'subtotal') continue;
    const amount = parseAmount(row['amount']);
    if (amount === null) continue;
    any = true;
    sum += kind === 'discount' ? -amount : amount;
  }
  return any ? Math.max(0, Math.round(sum * 100) / 100) : null;
}

export const dataTableBlock: BlockDef = {
  type: 'dataTable',
  name: 'Data table',
  description: 'Branded pricing table: rows with notes, discount and subtotal styling, total row.',
  category: 'data',
  icon: 'table',
  propsSchema: z.object({
    columns: z.array(z.object({
      key: z.string(), label: z.string(),
      align: z.enum(['left', 'right']).default('left'),
      format: z.enum(['currency', 'number', 'date']).optional(),
    })),
    rows: z.array(z.object({
      label: z.string(),
      note: z.string().optional(),
      amount: z.union([z.string(), z.number()]),
      kind: z.enum(['normal', 'discount', 'subtotal']).default('normal'),
    }).catchall(z.unknown())),
    totalLabel: z.string().optional(),
    totalValue: z.string().optional(),
  }),
  propFields: [
    {
      kind: 'list', name: 'columns', label: 'Columns',
      item: [
        { kind: 'text', name: 'key', label: 'Data key' },
        { kind: 'text', name: 'label', label: 'Header' },
        { kind: 'select', name: 'align', label: 'Align', options: ['left', 'right'], default: 'left' },
        { kind: 'select', name: 'format', label: 'Format', options: ['', 'currency', 'number', 'date'] },
      ],
    },
    {
      kind: 'list', name: 'rows', label: 'Rows',
      item: [
        { kind: 'text', name: 'label', label: 'Label' },
        { kind: 'text', name: 'note', label: 'Note (small text under label)', multiline: true },
        { kind: 'text', name: 'amount', label: 'Amount' },
        { kind: 'select', name: 'kind', label: 'Row style', options: ['normal', 'discount', 'subtotal'], default: 'normal' },
      ],
    },
    { kind: 'text', name: 'totalLabel', label: 'Total label' },
    { kind: 'text', name: 'totalValue', label: 'Total value' },
  ],
  styleFields: [],
  defaults: {
    props: {
      columns: [
        { key: 'label', label: 'Detail', align: 'left' },
        { key: 'amount', label: 'Amount', align: 'right' },
      ],
      rows: [{ label: 'Line item', amount: '$0.00', kind: 'normal' }],
      totalLabel: 'Total',
      totalValue: '$0.00',
    },
  },
  Component: function DataTable({ attrs, style }) {
    const columns = asArray(useProp('columns')) as ColumnDef[];
    const rows = asArray(useProp('rows'));
    const totalLabel = useTextProp('totalLabel');
    const totalValueProp = useTextProp('totalValue');
    // The total sums the rows live (so editing a line item re-adjusts it), and
    // only falls back to the bound `totalValue` when no row is a money amount.
    const computed = computeTotal(rows);
    const totalValue = computed !== null ? formatCell(computed, 'currency') : totalValueProp;
    return (
      <div {...attrs} className="invest" style={style}>
        <table>
          <thead>
            <tr>
              {columns.map((c, i) => (
                <th key={i} className={c.align === 'right' ? 'num' : undefined}>{asText(c.label)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri} className={ROW_CLASS[asText(row['kind'])]}>
                {columns.map((c, ci) => {
                  const key = asText(c.key);
                  return (
                    <td key={ci} className={c.align === 'right' ? 'num' : undefined}>
                      {c.format ? (
                        formatCell(row[key], c.format)
                      ) : (
                        <ItemTextSlot list="rows" index={ri} name={key} value={row[key]} />
                      )}
                      {ci === 0 && row['note'] !== undefined ? (
                        <ItemTextSlot list="rows" index={ri} name="note" value={row['note']} as="span" className="note" />
                      ) : null}
                    </td>
                  );
                })}
              </tr>
            ))}
            {totalLabel || totalValue ? (
              <tr className="total">
                <td colSpan={Math.max(columns.length - 1, 1)}>{totalLabel}</td>
                <td className="num">{totalValue}</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    );
  },
};

export const statBarBlock: BlockDef = {
  type: 'statBar',
  name: 'Stat bar',
  description: 'Horizontal strip of key figures with small labels.',
  category: 'data',
  icon: 'gauge',
  propsSchema: z.object({
    items: z.array(z.object({ value: z.string(), label: z.string() })),
  }),
  propFields: [
    {
      kind: 'list', name: 'items', label: 'Stats',
      item: [
        { kind: 'text', name: 'value', label: 'Value' },
        { kind: 'text', name: 'label', label: 'Label' },
      ],
    },
  ],
  styleFields: [],
  defaults: { props: { items: [{ value: '10', label: 'per team' }, { value: '2 hrs', label: 'duration' }] } },
  Component: function StatBar({ attrs, style }) {
    const items = asArray(useProp('items'));
    return (
      <div {...attrs} className="specs" style={style}>
        {items.map((item, i) => (
          <div className="spec dg-item" key={i}>
            <ItemTextSlot list="items" index={i} name="value" value={item['value']} as="div" className="v" />
            <ItemTextSlot list="items" index={i} name="label" value={item['label']} as="div" className="l" />
            <ListItemControls list="items" index={i} template={{ value: '—', label: 'label' }} />
          </div>
        ))}
      </div>
    );
  },
};

export const tocListBlock: BlockDef = {
  type: 'tocList',
  name: 'Contents list',
  description: 'Numbered table-of-contents rows with descriptions and page numbers.',
  category: 'data',
  icon: 'list-ordered',
  split: 'items',
  propsSchema: z.object({
    rows: z.array(z.object({
      n: z.string(), title: z.string(), desc: z.string().optional(), page: z.string().optional(),
      /** Resolved page key to link to (e.g. "intro", "program:0"); set by the projection. */
      anchor: z.string().optional(),
    })),
  }),
  propFields: [
    {
      kind: 'list', name: 'rows', label: 'Rows',
      item: [
        { kind: 'text', name: 'n', label: 'Number' },
        { kind: 'text', name: 'title', label: 'Title' },
        { kind: 'text', name: 'desc', label: 'Description', multiline: true },
        { kind: 'text', name: 'page', label: 'Page' },
      ],
    },
  ],
  styleFields: [],
  defaults: { props: { rows: [{ n: '01', title: 'Section', desc: 'What it covers.', page: '2' }] } },
  Component: function TocList({ attrs, style }) {
    const rows = asArray(useProp('rows'));
    // In the editing canvas rows stay plain <div>s so inline editing isn't
    // hijacked by link clicks; in the preview and PDF a row with an `anchor`
    // becomes a link that jumps to that page.
    const { editMode } = useEditState();
    return (
      <div {...attrs} className="toc" style={style}>
        {rows.map((row, i) => {
          const anchor = typeof row['anchor'] === 'string' ? row['anchor'] : '';
          const linked = !editMode && anchor !== '';
          const inner = (
            <>
              <ItemTextSlot list="rows" index={i} name="n" value={row['n']} as="div" className="toc-n" />
              <div className="toc-t">
                <ItemTextSlot list="rows" index={i} name="title" value={row['title']} as="b" />
                <ItemTextSlot list="rows" index={i} name="desc" value={row['desc']} as="span" />
              </div>
              <ItemTextSlot list="rows" index={i} name="page" value={row['page']} as="div" className="toc-p" />
              <ListItemControls list="rows" index={i} template={{ n: '00', title: 'New section', desc: '', page: '' }} />
            </>
          );
          return linked ? (
            <a className="toc-row toc-row--link dg-item" key={i} href={`#${pageAnchorId(anchor)}`}>
              {inner}
            </a>
          ) : (
            <div className="toc-row dg-item" key={i}>{inner}</div>
          );
        })}
      </div>
    );
  },
};

export const glanceBandBlock: BlockDef = {
  type: 'glanceBand',
  name: 'Glance band',
  description: 'Dark summary band with a label and key value/label pairs.',
  category: 'data',
  icon: 'panel-bottom',
  propsSchema: z.object({
    label: z.string(),
    items: z.array(z.object({ value: z.string(), label: z.string() })),
  }),
  propFields: [
    { kind: 'text', name: 'label', label: 'Label', default: 'At a glance' },
    {
      kind: 'list', name: 'items', label: 'Items',
      item: [
        { kind: 'text', name: 'value', label: 'Value' },
        { kind: 'text', name: 'label', label: 'Label' },
      ],
    },
  ],
  styleFields: [],
  defaults: { props: { label: 'At a glance', items: [{ value: '—', label: 'Detail' }] } },
  Component: function GlanceBand({ attrs, style }) {
    const items = asArray(useProp('items'));
    return (
      <div {...attrs} className="at-a-glance" style={style}>
        <TextSlot name="label" as="p" className="eyebrow" />
        <div className="glance-grid">
          {items.map((item, i) => (
            <div key={i} className="dg-item">
              <ItemTextSlot list="items" index={i} name="value" value={item['value']} as="div" className="v" />
              <ItemTextSlot list="items" index={i} name="label" value={item['label']} as="div" className="l" />
              <ListItemControls list="items" index={i} template={{ value: '—', label: 'Label' }} />
            </div>
          ))}
        </div>
      </div>
    );
  },
};

export const faqListBlock: BlockDef = {
  type: 'faqList',
  name: 'FAQ list',
  description: 'Question/answer pairs, in one or two columns.',
  category: 'data',
  icon: 'message-circle-question',
  split: 'items',
  propsSchema: z.object({
    items: z.array(z.object({ q: z.string(), a: z.string() })),
    columns: z.enum(['1', '2']).default('1'),
  }),
  propFields: [
    {
      kind: 'list', name: 'items', label: 'Questions',
      item: [
        { kind: 'text', name: 'q', label: 'Question' },
        { kind: 'text', name: 'a', label: 'Answer', multiline: true },
      ],
    },
    { kind: 'select', name: 'columns', label: 'Columns', options: ['1', '2'], default: '1' },
  ],
  styleFields: [],
  defaults: { props: { items: [{ q: 'Question?', a: 'Answer.' }], columns: '1' } },
  Component: function FaqList({ attrs, style }) {
    const items = asArray(useProp('items'));
    const two = useProp('columns') === '2';
    return (
      <div {...attrs} className={two ? 'faq-col' : undefined} style={style}>
        {items.map((item, i) => (
          <div className="faq dg-item" key={i}>
            <ItemTextSlot list="items" index={i} name="q" value={item['q']} as="h3" />
            <ItemTextSlot list="items" index={i} name="a" value={item['a']} as="p" />
            <ListItemControls list="items" index={i} template={{ q: 'New question?', a: 'Answer.' }} />
          </div>
        ))}
      </div>
    );
  },
};

export const stepsListBlock: BlockDef = {
  type: 'stepsList',
  name: 'Numbered steps',
  description: 'Numbered circle steps with titles and body text.',
  category: 'data',
  icon: 'list-checks',
  split: 'items',
  propsSchema: z.object({
    items: z.array(z.object({ n: z.string(), title: z.string(), body: z.string() })),
  }),
  propFields: [
    {
      kind: 'list', name: 'items', label: 'Steps',
      item: [
        { kind: 'text', name: 'n', label: 'Number' },
        { kind: 'text', name: 'title', label: 'Title' },
        { kind: 'text', name: 'body', label: 'Body', multiline: true },
      ],
    },
  ],
  styleFields: [],
  defaults: { props: { items: [{ n: '1', title: 'Step', body: 'What happens.' }] } },
  Component: function StepsList({ attrs, style }) {
    const items = asArray(useProp('items'));
    return (
      <div {...attrs} className="values" style={style}>
        {items.map((item, i) => (
          <div className="value dg-item" key={i}>
            <ItemTextSlot list="items" index={i} name="n" value={item['n']} as="div" className="n" />
            <div>
              <ItemTextSlot list="items" index={i} name="title" value={item['title']} as="h3" />
              <ItemTextSlot list="items" index={i} name="body" value={item['body']} as="p" />
            </div>
            <ListItemControls list="items" index={i} template={{ n: '0', title: 'New step', body: '' }} />
          </div>
        ))}
      </div>
    );
  },
};

export const checklistBlock: BlockDef = {
  type: 'checklist',
  name: 'Checklist',
  description: 'Titled group of ✓ checklist items.',
  category: 'data',
  icon: 'square-check',
  split: 'items',
  propsSchema: z.object({
    title: z.string(),
    items: z.array(z.object({ text: z.string() })),
  }),
  propFields: [
    { kind: 'text', name: 'title', label: 'Group title', default: 'Checklist' },
    {
      kind: 'list', name: 'items', label: 'Items',
      item: [{ kind: 'text', name: 'text', label: 'Item', multiline: true }],
    },
  ],
  styleFields: [],
  defaults: { props: { title: 'Checklist', items: [{ text: 'First thing to check' }] } },
  Component: function Checklist({ attrs, style }) {
    const items = asArray(useProp('items'));
    return (
      <div {...attrs} className="check-group" style={style}>
        <TextSlot name="title" as="h3" />
        {items.map((item, i) => (
          <div className="check dg-item" key={i}>
            <span className="tick">✓</span>
            <ItemTextSlot list="items" index={i} name="text" value={item['text']} as="div" />
            <ListItemControls list="items" index={i} template={{ text: 'New item' }} />
          </div>
        ))}
      </div>
    );
  },
};
